import BigNumber from 'bignumber.js';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, ColorPicker, DatePickerUI, Input } from 'sonata-react-components';
import { normalizeWithUnit } from 'sonata-static-storage/lib/helpers/i18nUnits.helpers';

import { getPaletteColor } from '@/helpers/ioSettings/flightColors.helper';
import { FLIGHT_TIMEZONE, getPastRules, hasFlightStarted, toDateOnly, toStoredDate } from '../flightSettings.helpers';
import { FlightStyled } from './Flight.styles';
import { useFlightCopies } from './useFlightCopies';

import type { DatePickerMarker, DatePickerOccupiedRange, DateRangeValue } from 'sonata-react-components';
import type { BaseCurrencyInterface } from 'sonata-static-storage/lib/schemas/baseCurrency/baseCurrency.schema';
import type { FlightState } from '../flightSettings.helpers';

type Props = {
  baseCurrency: BaseCurrencyInterface;
  errors?: Record<string, any>;
  index: number;
  ioIsPublished: boolean;
  item: FlightState;
  /** IO line items, drawn as lines at the bottom of the days. */
  markers: DatePickerMarker[];
  /** Every flight with dates; the one of `item` is flagged `current` here. */
  flightRanges: DatePickerOccupiedRange[];
  onChange: (flightId: string, patch: Partial<FlightState>) => void;
  onDelete: (flightId: string) => void;
};

export function Flight({
  baseCurrency,
  errors,
  flightRanges,
  index,
  ioIsPublished,
  item,
  markers,
  onChange,
  onDelete
}: Props) {
  const { formatMessage } = useIntl();
  const copies = useFlightCopies();
  const color = item.color ?? getPaletteColor(index);
  const occupiedRanges = useMemo(
    () => flightRanges.map(range => (range.id === item._id ? { ...range, current: true } : range)),
    [flightRanges, item._id]
  );
  const dateError = errors?.beginDate?.message || errors?.endDate?.message;
  const startDate = toDateOnly(item.beginDate);
  const endDate = toDateOnly(item.endDate);
  const hasStarted = hasFlightStarted(ioIsPublished, startDate);

  const handleDatesChange = (value: DateRangeValue) =>
    onChange(item._id, { beginDate: toStoredDate(value.startDate), endDate: toStoredDate(value.endDate) });

  const handleBudgetChange = (value: string) =>
    onChange(item._id, { goal: { ...item.goal, target: new BigNumber(value).decimalPlaces(4).toNumber() } });

  return (
    <FlightStyled className="flight">
      <ColorPicker
        className="flight__color"
        id={`flight-color-${item._id}`}
        labels={copies.colorPickerLabels}
        onChange={color => onChange(item._id, { color })}
        title={copies.colorPickerTitle}
        value={color}
      />
      <div className="flight__name">
        <Input
          description={errors?.name?.message}
          id={`flight-name-${item._id}`}
          inputStatus={errors?.name ? 'wrong' : 'default'}
          label={formatMessage({ defaultMessage: 'Name', id: 'spc.flightSettings.flight.name' })}
          name="name"
          onChange={value => onChange(item._id, { name: value as string })}
          placeholder={formatMessage({
            defaultMessage: 'Flight name',
            id: 'spc.flightSettings.flight.placeholder.name'
          })}
          shape="squareUI"
          type="text"
          value={item.name}
        />
      </div>
      <div className="flight__date">
        <DatePickerUI
          id={`insertion_order_flight_${index}_schedule`}
          label={copies.timeWindow}
          markerCoverageWarning={copies.markerCoverageWarning}
          markerName={copies.markerName}
          markers={markers}
          mode="range"
          occupiedRanges={occupiedRanges}
          onChange={handleDatesChange}
          timezone={FLIGHT_TIMEZONE}
          value={{ endDate, startDate }}
          {...getPastRules(ioIsPublished, startDate, endDate)}
        />
        {dateError && <div className="flight__dateError">{dateError}</div>}
      </div>
      <div className="flight__budget">
        <Input
          currencyConfig={baseCurrency}
          defaultValue={item.goal?.target}
          description={errors?.goal?.target?.message}
          id={`flight-budget-${item._id}`}
          inputStatus={errors?.goal?.target ? 'wrong' : 'default'}
          label={formatMessage({ defaultMessage: 'Budget', id: 'spc.flightSettings.flight.budget' })}
          name="Budget"
          numberFormat="currency"
          numberOfDecimals={2}
          onChange={value => handleBudgetChange(value as string)}
          placeholder={normalizeWithUnit(100, baseCurrency, 2)}
          shape="squareUI"
          type="number"
        />
      </div>
      <ButtonUI
        className="flight__delete"
        disabled={hasStarted || index === 0}
        icon="fa-solid fa-trash"
        onClick={() => onDelete(item._id)}
        shape="link"
        size="medium"
        type="secondary"
      />
    </FlightStyled>
  );
}
